/**
 * Popup Helper Utilities
 *
 * Centralised logic for evaluating whether a JSON-configured popup should be
 * displayed and for building the Redux alertPopUp state object.
 *
 * Previously this logic was duplicated inside desktopStepper.js,
 * mobileStepper.js and customAction.js.  Moving it here keeps the root
 * stepper files clean — they remain generic and shared across every
 * insurance wizard — while new wizards can still add popup behaviour purely
 * through their JSON configuration.
 */

import { getRef } from '../../../../utils/functions';
import { apiCallWizard } from '../../../../actions/wizards';
import { updateTempState } from '../../../../actions/temp';
import { getAllData } from '../../functions/apiCall';
import routes from '../../../../utils/variables/routes';
import { checkComponent } from '../../functions/misc';

/* ------------------------------------------------------------------ */
/*  shouldDisplayPopup                                                 */
/* ------------------------------------------------------------------ */

/**
 * Evaluate the conditional fields on a customAction (from JSON config) and
 * decide whether the popup should be shown.
 *
 * Supports both:
 *  - A single top-level conditionalKey/Value/Operator check.
 *  - A `conditions` array (same schema as component conditions) evaluated
 *    via `checkComponent` with `defaultHide = true`.
 *
 * @param {Object} customAction  – the `next.customAction` (or top-level
 *   `customAction`) object from the wizard JSON.
 * @param {Object} storeState    – the full Redux store state.
 * @returns {boolean}
 */
export const shouldDisplayPopup = (customAction, storeState) => {
  if (!customAction || customAction?.actionType !== 'popup') {
    return false;
  }

  // When a conditions array is present, delegate to checkComponent
  if (customAction?.conditions && customAction.conditions.length > 0) {
    const props = {
      isConditionalComponent: customAction?.isConditionalComponent ?? true,
      conditionalKey: customAction?.conditionalKey || null,
      conditionalValue: customAction?.conditionalValue || null,
      conditionalOperator: customAction?.conditionalOperator || null,
      conditionalFieldType: customAction?.conditionalFieldType || null,
      conditionalAction: customAction?.conditionalAction || 'show',
      conditions: customAction.conditions,
    };
    const defaultHide = customAction?.defaultHide ?? true;
    const { show } = checkComponent(props, storeState, true, true, defaultHide);
    return show;
  }

  // No condition configured → always show the popup
  if (!customAction?.isConditionalComponent || !customAction?.conditionalKey) {
    return true;
  }

  /* --- resolve the left-hand value for the comparison --- */
  const leftValue = getRef(storeState, customAction.conditionalKey);

  /* --- normalise both sides ----------------------------------- */
  const operator = customAction?.conditionalOperator || 'equal';
  const fieldType = customAction?.conditionalFieldType || 'string';

  const normalize = value =>
    fieldType === 'string' && typeof value === 'string'
      ? value.trim().toLowerCase()
      : value;

  const normalizedLeft = normalize(leftValue);
  const normalizedRight = normalize(customAction?.conditionalValue);

  /* --- operator evaluation ------------------------------------ */
  switch (operator) {
    case 'equal':
      return normalizedLeft === normalizedRight;
    case 'not_equal':
      return normalizedLeft !== normalizedRight;
    default:
      return false;
  }
};

/* ------------------------------------------------------------------ */
/*  buildPopupAlertState                                               */
/* ------------------------------------------------------------------ */

/**
 * Build the `alertPopUp` object that gets dispatched into Redux temp state
 * so the global `AlertPopUpModal` can render the popup.
 *
 * Every value is read from the JSON config — nothing is hard-coded.
 *
 * The `dismissiveAction` is returned as a pre-built object with an
 * `onClick` handler so that `alertPopUpModal` stays a pure pass-through
 * (no motor-specific logic in that shared file).
 *
 * @param {Object} customAction – JSON customAction config
 * @param {Object} deps         – { dispatch, store, router }
 * @returns {Object}  ready-to-dispatch alertPopUp payload
 */
export const buildPopupAlertState = (customAction, { dispatch, store, router } = {}) => {
  const actionType =
    customAction?.popupDismissiveAction?.actionType || null;
  const apiCalls =
    customAction?.popupDismissiveAction?.apiCalls || [];
  const redirectUrl =
    customAction?.popupDismissiveAction?.redirectUrl || null;
  const label =
    customAction?.popupDismissiveAction?.label ||
    customAction?.popupDismissiveAction ||
    'Submit';

  /**
   * onClick handler for the dismissive button.
   * - When actionType is 'apiCall': execute apiCalls then redirect.
   * - Otherwise: just close the popup (handled by ModalAlert default).
   */
  const onClick = async () => {
    if (actionType === 'apiCall' && apiCalls.length > 0 && dispatch && store) {
      try {
        const storeState = store.getState();
        await getAllData(
          apiCalls,
          apiCallWizard.apiCallWizardAction,
          dispatch,
          storeState,
        );
      } catch (error) {
        console.error('Error executing popup apiCalls:', error);
      }
    }

    if (dispatch) {
      dispatch(updateTempState.updateTempStateAction({ alertPopUp: null }));
    }

    if (router && (actionType === 'apiCall' || redirectUrl)) {
      router.push(redirectUrl || routes.thankYou);
    }
  };

  return {
    title: customAction?.popupTitle || 'Notice',
    content: customAction?.popupContent || 'This is a popup.',
    dismissiveAction: {
      label,
      onClick,
    },
    confirmingAction: customAction?.popupConfirmingAction,
    setCloseAsDismissiveAction: true,
    maxWidth: customAction?.popupMaxWidth || 'sm',
  };
};
